import { createSelector } from '@reduxjs/toolkit'

import type { QueryResultLike } from '@krp/client-runtime'

import type { CommandFeedback } from '../../../shared/model/feedback'
import { makeQueryViewSelector, readyData } from '../../../shared/model/query'
import { deviceRowOf, snapshotText } from '../../catalog/model/selectors'
import type { AqsiDevice, AqsiDevices } from '../../catalog/model/types'
import type { StructureShopVm, StructureVm } from './types'

export interface StructureVmInput {
  devices: QueryResultLike<AqsiDevices>
  /** Итог команды кабинета этого rid — тот же, что у каталога: загрузка касс одна на оба окна. */
  feedback: CommandFeedback | undefined
}

const NO_SHOP = 'Без магазина'
const REFRESH_COMMAND = 'devices.refresh'
const EMPTY: readonly StructureShopVm[] = []

/** Кассы по магазинам: магазины по имени, кассы без магазина — последними, кассы внутри — по номеру. */
function shopsOf(devices: readonly AqsiDevice[]): readonly StructureShopVm[] {
  if (devices.length === 0) return EMPTY
  const byShop = new Map<string, { name: string; devices: AqsiDevice[] }>()
  for (const device of devices) {
    const id = device.shopId ?? (device.shopName === null ? '' : `name:${device.shopName}`)
    const shop = byShop.get(id) ?? { name: device.shopName ?? (id === '' ? NO_SHOP : `Магазин ${id}`), devices: [] }
    shop.devices.push(device)
    byShop.set(id, shop)
  }
  return [...byShop.entries()]
    .sort(([leftId, left], [rightId, right]) =>
      leftId === '' ? 1 : rightId === '' ? -1 : left.name.localeCompare(right.name, 'ru'),
    )
    .map(([id, shop]) => ({
      id,
      name: shop.name,
      devices: [...shop.devices]
        .sort((left, right) => left.serialNumber.localeCompare(right.serialNumber, 'ru'))
        .map(deviceRowOf),
    }))
}

function noticeOf(feedback: CommandFeedback | undefined): StructureVm['notice'] {
  if (feedback === undefined || feedback.command !== REFRESH_COMMAND || feedback.phase === 'sending') return null
  if (feedback.phase === 'done') return feedback.note === null ? null : { kind: 'done', text: feedback.note }
  return { kind: 'failed', text: feedback.note ?? feedback.error?.message ?? 'Кассы не загружены' }
}

export const makeSelectStructureVm = () => {
  const selectView = makeQueryViewSelector(
    (input: StructureVmInput) => input.devices,
    (data: AqsiDevices) => data.items.length === 0,
  )
  const selectShops = createSelector([selectView, (input: StructureVmInput) => input.devices.data], (view, data) =>
    shopsOf(readyData(view, data)?.items ?? []),
  )
  return createSelector(
    [
      selectView,
      selectShops,
      (input: StructureVmInput) => input.devices.data,
      (input: StructureVmInput) => input.feedback,
    ],
    (query, shops, data, feedback): StructureVm => {
      const sending = feedback?.phase === 'sending' ? feedback.command : null
      const deviceCount = shops.reduce((sum, shop) => sum + shop.devices.length, 0)
      return {
        query,
        snapshot: data !== undefined ? snapshotText(data.materialization) : '',
        summary: data === undefined ? null : `Магазинов: ${shops.length}, касс: ${deviceCount}`,
        shops,
        refreshing: sending === REFRESH_COMMAND,
        busy: sending !== null,
        notice: noticeOf(feedback),
      }
    },
  )
}
